import React from 'react';

import { Typography } from '@mui/material';
import type { GridToolbarContainerProps } from '@mui/x-data-grid-premium';
import {
  gridRowSelectionIdsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { X } from 'lucide-react';
import type { DisplayMode, TotalDocumentCount } from 'src/components';
import {
  AlineTooltip,
  FiltersToolbarButton,
  FloatingActionButton,
  ReportActions,
  SearchInput,
  SelectButton,
  Spacer,
} from 'src/components';
import { DisplayMenu } from 'src/components/shared/DisplayMenu';
import { doNothing } from 'src/helpers';
import type { TrashDocuments } from 'src/hooks';
import { useDataGridSearch } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { AiStatusFilter } from './AiStatusfilter';
import { RunAIDialog } from './RunAIDialog';
import { StopAIDialog } from './StopAIDialog';

const useStyles = makeStyles()(theme => ({
  discardIcon: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    borderRadius: 4,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'inline-flex',
    justifyContent: 'center',
    marginLeft: theme.spacing(0.5),
    padding: 2,
  },
  saveChangesButton: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    gap: theme.spacing(0.5),
    height: 32,
    padding: theme.spacing(0, 0.5, 0, 1),
  },
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
}));

export interface ReportToolbarProps extends GridToolbarContainerProps {
  displayMode?: DisplayMode;
  hasUnsavedChanges?: boolean;
  onClose?: () => void;
  onCloseStop?: () => void;
  onDiscardChanges?: () => void;
  onDisplayModeChange?: (mode: DisplayMode) => void;
  onRowGroupingModelChange?: (model: string[]) => void;
  onSaveChanges?: () => Promise<void>;
  open?: boolean;
  reportId?: UUID;
  reportName?: string;
  rowCount?: number;
  rowGroupingModel?: readonly string[];
  stopOpen?: boolean;
}

export function ReportToolbar({
  displayMode = 'flat',
  hasUnsavedChanges = false,
  onClose = doNothing,
  onCloseStop = doNothing,
  onDiscardChanges,
  onDisplayModeChange,
  onRowGroupingModelChange,
  onSaveChanges,
  open = false,
  reportId = '',
  reportName = '',
  rowCount = 0,
  rowGroupingModel = [],
  stopOpen = false,
}: ReportToolbarProps): JSX.Element | null {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const { search, setSearch } = useDataGridSearch();

  const handleSaveChanges = async () => {
    if (!onSaveChanges) return;
    await onSaveChanges();
    apiRef.current.hideFilterPanel();
  };

  const showSaveButton = Boolean(onSaveChanges) && hasUnsavedChanges;

  if (!reportId) return null;

  return (
    <div className={classes.toolbar}>
      <FiltersToolbarButton />
      <ReportActions reportId={reportId} reportName={reportName} />
      <AiStatusFilter reportId={reportId} />
      <Spacer />
      {showSaveButton && (
        <SelectButton
          className={classes.saveChangesButton}
          variant="outlined"
          onClick={handleSaveChanges}
        >
          <Typography color="inherit" sx={{ fontWeight: 600 }} variant="subtitle2">
            Save Changes
          </Typography>
          {onDiscardChanges && (
            <AlineTooltip arrow placement="top" title="Discard changes">
              <span
                className={classes.discardIcon}
                role="button"
                tabIndex={0}
                onClick={event => {
                  event.stopPropagation();
                  onDiscardChanges();
                }}
              >
                <X size={14} />
              </span>
            </AlineTooltip>
          )}
        </SelectButton>
      )}
      <DisplayMenu
        displayMode={displayMode}
        rowGroupingModel={rowGroupingModel}
        onDisplayModeChange={onDisplayModeChange}
        onRowGroupingModelChange={onRowGroupingModelChange}
      />
      <SearchInput
        aria-label="Search documents"
        placeholder="Search documents..."
        search={search}
        setSearch={setSearch}
      />
      <RunAIDialog open={open} rowCount={rowCount} onClose={onClose} />
      <StopAIDialog open={stopOpen} rowCount={rowCount} onClose={onCloseStop} />
    </div>
  );
}

export interface ReportFooterProps {
  isRelationshipGrouping?: boolean;
  missingMatchesOnly?: boolean;
  onDocumentsTrashed?: (documentIds: readonly UUID[]) => void;
  onGroupTrashed?: (groupRowId: string, trashedCount: number) => void;
  reportId?: UUID;
  showRowsLockAction?: boolean;
  totalDocumentCount?: TotalDocumentCount;
  updateCacheOnTrash?: TrashDocuments['updateCacheOnTrash'];
}

export function ReportFooter({
  isRelationshipGrouping = false,
  missingMatchesOnly = false,
  onDocumentsTrashed,
  onGroupTrashed,
  reportId,
  showRowsLockAction = true,
  totalDocumentCount,
  updateCacheOnTrash,
}: ReportFooterProps): JSX.Element | null {
  const apiRef = useGridApiContext();
  const selectionModel = useGridSelector(apiRef, gridRowSelectionIdsSelector);

  if (!selectionModel.size) return null;

  return (
    <FloatingActionButton
      isRelationshipGrouping={isRelationshipGrouping}
      missingMatchesOnly={missingMatchesOnly}
      reportId={reportId}
      showRowsLockAction={showRowsLockAction}
      totalDocumentCount={totalDocumentCount ?? 0}
      updateCacheOnTrash={updateCacheOnTrash}
      onDocumentsTrashed={onDocumentsTrashed}
      onGroupTrashed={onGroupTrashed}
    />
  );
}
